/**
 * REST API klient pro ovládání Foscam kamer (PTZ, zoom, presety, kodek).
 * Tenké axios wrappery — CSRF + credentials řeší axios instance automaticky.
 */

import { API_URL } from "@client/utils/apiUrl";
import { deleteAsync, getAsync, postAsync, postKeepalive } from "./axios";

export type PtzDirection = "Up" | "Down" | "Left" | "Right" | "TopLeft" | "TopRight" | "BottomLeft" | "BottomRight";

export const getFoscamCaps = () => getAsync<ApiResp<FoscamCaps[]>>("/api/foscam/caps");

export interface FoscamIceConfig {
  iceServers: RTCIceServer[];
}

/** ICE config (STUN + ephemeral TURN) pro WebRTC přehrávač. */
export const getFoscamIce = () => getAsync<ApiResp<FoscamIceConfig>>("/api/foscam/ice");

export const getFoscamPresets = (slug: string) =>
  getAsync<ApiResp<{ presets: string[] }>>(`/api/foscam/${slug}/presets`);

export const postPtzMove = (slug: string, direction: PtzDirection) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/ptz/move`, { direction });

export const postPtzStop = (slug: string) => postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/ptz/stop`);

/**
 * Stop, který přežije zavření stránky (`pagehide`). Zásadní pojistka: bez něj by
 * zavření záložky během držení tlačítka nechalo kameru točit se dál.
 */
export const postPtzStopKeepalive = (slug: string): void =>
  postKeepalive(`${API_URL}/api/foscam/${slug}/ptz/stop`);

/** Zoom stop varianta téhož — zoom má vlastní CGI příkaz. */
export const postZoomStopKeepalive = (slug: string): void =>
  postKeepalive(`${API_URL}/api/foscam/${slug}/zoom`, { dir: "stop" });

export const postZoom = (slug: string, dir: "in" | "out" | "stop") =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/zoom`, { dir });

/**
 * Otočit kameru tak, aby bod (x, y) skončil uprostřed. Souřadnice jsou normalizované
 * (0..1 od levého horního rohu OBRAZU, ne dlaždice).
 *
 * Odpovídá se hned — samotné míření trvá 4–8 s a průběh chodí přes socket `foscam:aim`.
 */
export const postPtzAim = (slug: string, x: number, y: number) =>
  postAsync<ApiResp<{ aimId: string; slug: string }>>(`/api/foscam/${slug}/ptz/aim`, { x, y });

/** Zruší probíhající míření (a pro jistotu pošle i stop). */
export const deletePtzAim = (slug: string) =>
  deleteAsync<ApiResp<{ cancelled: boolean }>>(`/api/foscam/${slug}/ptz/aim`);

export const postPtzSpeed = (slug: string, speed: number) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/ptz/speed`, { speed });

/** Aktuální rychlost PTZ z kamery — ať Nastavení ukazuje skutečnou hodnotu, ne odhad. */
export const getPtzSpeed = (slug: string) => getAsync<ApiResp<{ speed: number }>>(`/api/foscam/${slug}/ptz/speed`);

/** Runtime ffmpeg workerů (diagnostika streamů v Nastavení → Kamery). Admin. */
export const getFoscamStreams = () => getAsync<ApiResp<FoscamStreamRuntime[]>>("/api/foscam/streams");

export const postPresetGoto = (slug: string, name: string) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/preset/goto`, { name });

export const postPresetSave = (slug: string, name: string) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/preset/save`, { name });

export const deletePreset = (slug: string, name: string) =>
  deleteAsync<ApiResp<unknown>>(`/api/foscam/${slug}/preset`, { name });

export const postCodec = (slug: string, codec: "h264" | "h265") =>
  postAsync<ApiResp<{ codec: string; workerRestarted: boolean; caps: FoscamCaps }>>(`/api/foscam/${slug}/codec`, {
    codec,
  });

/** Kompletní nastavení kamery pro Settings panel (model, stream, image, osd, mirror, ir, hdr). */
export const getFoscamSettings = (slug: string) =>
  getAsync<ApiResp<FoscamSettings>>(`/api/foscam/${slug}/settings`);

/** Částečná změna obrazu — pošli jen měněné klíče, server volá individuální settery. */
export const postFoscamImage = (slug: string, patch: Partial<FoscamImageSettings>) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/image`, patch);

/** Reset obrazu na tovární default (resetImageSetting, bez parametrů). */
/**
 * Restart kamery. Odpověď přijde i tehdy, když kamera stihne zavřít spojení dřív —
 * server rozpojení během restartu počítá jako úspěch (viz `foscamRouter`).
 */
export const postFoscamReboot = (slug: string) =>
  postAsync<ApiResp<{ result: number | null }>>(`/api/foscam/${slug}/reboot`);

export const postFoscamImageReset = (slug: string) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/image/reset`);

/** OSD nastavení (server dělá RMW: přečte get, změní, pošle vše). */
export const postFoscamOsd = (
  slug: string,
  patch: { timeStamp?: boolean; devName?: boolean; tempHumid?: boolean; osdMask?: boolean; dispPos?: number },
) => postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/osd`, patch);

/** Zrcadlení / převrácení obrazu (mirrorVideo / flipVideo). */
export const postFoscamMirror = (slug: string, patch: { isMirror?: boolean; isFlip?: boolean }) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/mirror`, patch);

/** IR přisvícení — mode 0=auto, 1=manual; ledOn řídí ruční zapnutí/vypnutí. */
export const postFoscamIr = (slug: string, body: { mode: number; ledOn?: boolean }) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/ir`, body);

/** HDR mód (jen MCS4020; ostatní modely vrací result=-3). */
export const postFoscamHdr = (slug: string, mode: number) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/hdr`, { mode });

/** Aktivní streamType (0-3) pro main nebo sub stream. */
export const postFoscamStreamType = (slug: string, body: { sub: boolean; streamType: number }) =>
  postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/stream/type`, body);

/** Parametry video profilu (server dělá RMW helper setVideoProfile, posílá všech 6 parametrů). */
export const postFoscamStreamParam = (
  slug: string,
  body: {
    sub: boolean;
    streamType: number;
    resolution?: number;
    bitRate?: number;
    frameRate?: number;
    gop?: number;
    isVBR?: number;
  },
) => postAsync<ApiResp<unknown>>(`/api/foscam/${slug}/stream/param`, body);
